/* eslint-disable react/jsx-no-target-blank */
import React from 'react';
import isEmpty from 'lodash/isEmpty';

import Wysiwyg from 'components/blocks/wysiwyg/Wysiwyg';
import ResponsiveImage from 'components/base/responsiveImage/ResponsiveImage';
import DefaultErrorBoundary from 'components/error-boundaries/DefaultErrorBoundary';
import useTokenReplacement from 'hooks/custom/useTokenReplacement';
import { ImageObj } from 'types';
import { logTmsButtonClick } from 'utils/analytics/logTmsEvent';

interface ResponsiveImageSizes {
  MOBILE: string;
  TABLET: string;
  DESKTOP: string;
  DEFAULT: string;
}

interface CTA {
  useCTA: boolean;
  buttonText: string;
  link: string;
  buttonActionType:
    | 'customLink'
    | 'pageLink'
    | 'clickPortalWizard'
    | 'fullPageForm';
  handleClickPortalWizardToggle?: () => void;
  router?: {
    asPath: string;
  };
  linkParams?: string;
}

interface FeatureBlockProps {
  featureImage: { image: ImageObj };
  title: string;
  description: string;
  cta: CTA;
  wrapBlockWithCtaLink?: boolean;
  responsiveImageSizes: ResponsiveImageSizes;
}

function FeatureBlock({
  featureImage,
  title,
  description,
  cta,
  wrapBlockWithCtaLink = true,
  responsiveImageSizes,
}: FeatureBlockProps): React.JSX.Element {
  const tokenizedLink = useTokenReplacement(cta.link);

  const blockContent = (
    <>
      {!isEmpty(featureImage) && (
        <div className="featureBlocks__blockIcon">
          <DefaultErrorBoundary meta={{ component: 'ResponsiveImage' }}>
            <ResponsiveImage
              alt={title}
              className="featureBlocks__iconImg"
              images={featureImage}
              loading="lazy"
              sizes={responsiveImageSizes}
            />
          </DefaultErrorBoundary>
        </div>
      )}
      {title && <div className="featureBlocks__blockTitle">{title}</div>}
      {description && (
        <div className="featureBlocks__blockDescription">
          <Wysiwyg content={description} />
        </div>
      )}
    </>
  );

  function handleButtonClickTracking(): void {
    if (cta.buttonText) {
      logTmsButtonClick(cta.buttonText);
    }
  }

  function getBlockLinkContent(): React.JSX.Element {
    if (wrapBlockWithCtaLink) {
      return (
        <a
          className="featureBlocks__block"
          href={tokenizedLink}
          target={cta.buttonActionType === 'customLink' ? '_blank' : undefined}
          rel={
            cta.buttonActionType === 'customLink'
              ? 'noreferrer noopener'
              : undefined
          }
        >
          {blockContent}
        </a>
      );
    }
    return (
      <div className="featureBlocks__block">
        {blockContent}
        <a
          className="featureBlocks__cta"
          href={tokenizedLink}
          onClick={handleButtonClickTracking}
          onAuxClick={handleButtonClickTracking}
          onContextMenu={handleButtonClickTracking}
          target={cta.buttonActionType === 'customLink' ? '_blank' : undefined}
          rel={
            cta.buttonActionType === 'customLink'
              ? 'noreferrer noopener'
              : undefined
          }
        >
          {cta.buttonText}
        </a>
      </div>
    );
  }

  switch (cta.useCTA && cta.buttonActionType) {
    case 'customLink': {
      return getBlockLinkContent();
    }

    case 'pageLink': {
      return getBlockLinkContent();
    }

    case 'clickPortalWizard': {
      return (
        <button
          type="button"
          className="featureBlocks__block"
          data-testid={`featureBlockCtaButton--${cta.buttonActionType}`}
          onClick={cta.handleClickPortalWizardToggle}
        >
          {blockContent}
        </button>
      );
    }

    case 'fullPageForm': {
      const urlPath = cta.router?.asPath || 'default/landing';

      return (
        <a
          className="featureBlocks__block"
          data-testid={`featureBlockCtaButton--${cta.buttonActionType}`}
          href={`${urlPath}/form${cta.linkParams}`}
        >
          {blockContent}
        </a>
      );
    }

    default: {
      return <div className="featureBlocks__block">{blockContent}</div>;
    }
  }
}

export default FeatureBlock;
